import { useQuery, QueryStatus, queryCache } from "react-query";
import { formatStartEndDates } from "lib/Date";
import {
  CateringEventStatus,
  ICateringEventView,
  ICateringEventDetailedView,
} from "types/CateringEvent";
import {
  listCateringEvents,
  getCateringEvent,
  getEventMenuDetails,
} from "api/CateringApi";

interface IQueryCateringEvents {
  status: QueryStatus;
  eventsData: { cateringEvents: ICateringEventView[] } | null | undefined;
  isFetching: boolean;
}

export const useQueryCateringEvents = (
  startDate: Date,
  endDate: Date,
  branchId?: string,
  eventStatus?: CateringEventStatus
): IQueryCateringEvents => {
  const [start, end] = formatStartEndDates(startDate, endDate, true);
  const {
    status,
    data,
    isFetching,
  } = useQuery(
    ["cateringEvents", start, end],
    async () => listCateringEvents(start, end)
  );

  // Client-side filtering (backend doesn't support branchId/status query params)
  const all = data?.cateringEvents ?? [];
  const filtered = all.filter(
    (e) =>
      (!branchId || e.branchId === branchId) &&
      (!eventStatus || e.status === eventStatus)
  );

  return {
    status,
    eventsData: data !== undefined ? { cateringEvents: filtered } : undefined,
    isFetching,
  };
};

interface IQueryCateringEvent {
  status: QueryStatus;
  eventData: { cateringEvent: ICateringEventView } | null | undefined;
  isFetching: boolean;
}

export const useQueryCateringEvent = (
  eventId: string
): IQueryCateringEvent => {
  const {
    status,
    data: eventData,
    isFetching,
  } = useQuery(["cateringEvent", eventId], async () => {
    return await getCateringEvent(eventId);
  });
  return { status, eventData, isFetching };
};

interface IQueryCateringEventMenuDetails {
  menuDetailsStatus: QueryStatus;
  menuDetails: ICateringEventDetailedView | null | undefined;
  menuDetailsFetching: boolean;
}

export const useQueryCateringEventMenuDetails = (
  eventId: string
): IQueryCateringEventMenuDetails => {
  const {
    status: menuDetailsStatus,
    data: menuDetails,
    isFetching: menuDetailsFetching,
  } = useQuery(["cateringEventMenuDetails", eventId], async () => {
    return await getEventMenuDetails(eventId);
  });
  return { menuDetailsStatus, menuDetails, menuDetailsFetching };
};

export const invalidateCateringEventsCache = () => {
  queryCache.invalidateQueries("cateringEvents");
  queryCache.invalidateQueries("cateringEvent");
  queryCache.invalidateQueries("cateringEventMenuDetails");
};
